import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Idempotent loaders — Settings can re-render many times as data refreshes,
// but the tracking scripts must only ever be injected once per ID.
let loadedGaId = null;
let loadedPixelId = null;

export function loadGoogleAnalytics(measurementId) {
  if (!measurementId || loadedGaId === measurementId) return;
  loadedGaId = measurementId;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // gtag's own config call sends the pageview for whatever URL is current
  // right when GA finishes loading (which, since it loads async after
  // settings arrive, may not be the moment the app first mounted).
  // AnalyticsRouteTracker only fires on *subsequent* SPA navigations, so
  // between the two, every page view — including the first — is covered
  // exactly once.
  window.gtag("config", measurementId);
}

export function loadMetaPixel(pixelId) {
  if (!pixelId || loadedPixelId === pixelId) return;
  loadedPixelId = pixelId;

  if (!window.fbq) {
    /* eslint-disable */
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
      t = b.createElement(e); t.async = true; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
  }
  window.fbq("init", pixelId);
  window.fbq("track", "PageView");

  if (!document.getElementById("meta-pixel-noscript")) {
    const noscript = document.createElement("noscript");
    noscript.id = "meta-pixel-noscript";
    const img = document.createElement("img");
    img.height = 1;
    img.width = 1;
    img.style.display = "none";
    img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  }
}

export function trackPageView(pathWithSearch) {
  if (loadedGaId && window.gtag) {
    window.gtag("event", "page_view", {
      page_path: pathWithSearch,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
  if (loadedPixelId && window.fbq) {
    window.fbq("track", "PageView");
  }
}

// Fire on genuine lead-gen form submissions (registration, contact) so Meta's
// Events Manager sees the standard "Lead" event, not just PageView. GA4 gets
// a matching "generate_lead" event for the same reason.
export function trackLead(contentName, params = {}) {
  if (loadedPixelId && window.fbq) {
    window.fbq("track", "Lead", { content_name: contentName, ...params });
  }
  if (loadedGaId && window.gtag) {
    window.gtag("event", "generate_lead", { lead_type: contentName, ...params });
  }
}

// Mounted once inside <BrowserRouter> — fires a pageview on every client-side
// navigation, since GA/Meta's own scripts only see the initial hard load.
export function AnalyticsRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);
  return null;
}
